import { CommonModule } from "@angular/common";
import { Component, computed, input, output } from "@angular/core";
import { TagModule } from "primeng/tag";
import { JobStatus } from "../../../../../ccm/BQ7/job-schedule/job-status";
import { ScheduleLotCard } from "../../models/ScheduleLotCard";
import { ScheduleJobDetail } from "../../models/ScheduleJobDetail";
import { formatMonthDay } from "../../../../../../shared/utils/formatting-helper";
import { LotPrimaryIdentifier } from "../../../../../../core/feature-flags/models/AdminConfigDto";

export interface SyncStatusBadge {
  severity: "success" | "info" | "warn";
  icon: string;
  label: string;
  testId: string;
}

/** Overdue is surfaced as its own badge, so it is deliberately absent from this map. */
const syncStatusBadges: Readonly<Record<string, SyncStatusBadge>> = {
  [JobStatus.CompleteUploaded]: {
    severity: "success",
    icon: "pi pi-check-circle",
    label: "Uploaded",
    testId: "cm-job-schedule-badge-uploaded"
  },
  [JobStatus.CompleteInProgress]: {
    severity: "info",
    icon: "pi pi-sync",
    label: "Upload in Progress",
    testId: "cm-job-schedule-badge-upload-in-progress"
  },
  [JobStatus.CompletePending]: {
    severity: "info",
    icon: "pi pi-sync",
    label: "Upload in Progress",
    testId: "cm-job-schedule-badge-upload-pending"
  },
  [JobStatus.CompleteOffline]: {
    severity: "warn",
    icon: "pi pi-sync",
    label: "Upload Pending",
    testId: "cm-job-schedule-badge-upload-offline"
  }
};

@Component({
  selector: "app-schedule-lot-card",
  standalone: true,
  imports: [CommonModule, TagModule],
  templateUrl: "./schedule-lot-card.component.html",
  styleUrl: "./schedule-lot-card.component.scss"
})
export class ScheduleLotCardComponent {
  readonly card = input.required<ScheduleLotCard>();
  /** Resolves each step's upload state; the page owns the sync metadata. */
  readonly syncStatusOf = input<(job: ScheduleJobDetail) => JobStatus | null>(() => null);
  /** Sections that span a single day already state the date in their header. */
  readonly showDate = input(false);
  /** Which identifier the division leads with; the other one drops to the secondary line. */
  readonly lotPrimaryIdentifier = input<LotPrimaryIdentifier>(LotPrimaryIdentifier.LotBlock);

  readonly cardClick = output<ScheduleLotCard>();
  readonly syncStatusClick = output<ScheduleJobDetail>();

  private readonly isAddressPrimary = computed(() =>
    this.lotPrimaryIdentifier() === LotPrimaryIdentifier.Address);

  // The card's own label; the model keeps the lot block bare so it still sorts numerically.
  private readonly lotLabel = computed(() => `Lot ${this.card().lotBlockText}`);

  readonly primaryText = computed(() =>
    (this.isAddressPrimary() ? this.card().streetAddress : this.lotLabel()));

  readonly secondaryText = computed(() =>
    [this.card().communityName, this.isAddressPrimary() ? this.lotLabel() : this.card().streetAddress]
      .filter(Boolean)
      .join(" \u00B7 "));

  readonly dateSuffix = computed(() =>
    (this.showDate() ? ` \u2022 ${formatMonthDay(this.card().effectiveDate)}` : ""));

  syncBadgeFor(job: ScheduleJobDetail): SyncStatusBadge | null {
    const status = this.syncStatusOf()(job);

    return status ? syncStatusBadges[status] ?? null : null;
  }

  onSyncStatusClick(event: Event, job: ScheduleJobDetail): void {
    event.stopPropagation();
    this.syncStatusClick.emit(job);
  }
}
